import { useQuery, useReadQuery } from '@apollo/client';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { StickyTabNav } from '@synoptic/ui-kit/tab-nav/sticky-tab-nav.js';
import { Suspense, useEffect, useState } from 'react';
import {
  ClientLoaderFunction,
  Outlet,
  ShouldRevalidateFunction,
  useParams,
} from 'react-router';
import { shouldReportGraphQLError } from '@/apollo/graphql-error-reporting.ts';
import { makeSsrApolloClient } from '@/apollo/ssr-apollo-client.ts';
import { ErrorPage } from '@/components/error-page/error-page.tsx';
import { InteractionRequiredModal } from '@/components/interaction-required-modal/interaction-required-modal.tsx';
import { Page, PageContent } from '@/components/layout/page.tsx';
import { PageHeader } from '@/components/page-header/page-header.tsx';
import { NudgeFallback } from '@/components/streams/nudge-card/nudge-fallback.tsx';
import { StreamDeckNudge } from '@/components/streams/nudge-card/stream-deck-nudge/stream-deck-nudge.tsx';
import { getStreamNotifSoundStatus } from '@/components/streams/toggle-notif-sound/stream-notif-sound-status.ts';
import { adminTab } from '@/components/tab-link.css.ts';
import { TabLink } from '@/components/tab-link.tsx';
import { SoundStatus } from '@/components/toggle-sound/sound-status.ts';
import { flags } from '@/feature-flags/flags.ts';
import { useFeatureFlags } from '@/feature-flags/use-feature-flags.ts';
import { tokenProviderContext } from '@/request-context.ts';
import { getPageMeta } from '@/routes-utils/get-page-meta.ts';
import { paths } from '@/routes-utils/paths.ts';
import { checkUserInteractionWithAudio } from '@/utils/audio-utils/check-user-interaction-with-audio.ts';
import { StreamHasTimeSeriesQuery } from '../_app.p.streams_.$id.time-series/queries.ts';
import { tournamentNav } from '../_app.p.tournaments_.$id/page.css.ts';
import { useAuthInfo } from '../_app/use-auth-info.ts';
import { Route } from './+types/route.ts';
import { getStreamPageMeta } from './get-stream-page-meta.ts';
import {
  AuthenticatedStreamLoaderData,
  streamLoader,
  useStreamLoaderData,
} from './loader.ts';
import { StreamBannerFallback } from './stream-banner/stream-banner-fallback.tsx';
import { StreamBanner } from './stream-banner/stream-banner.tsx';
import { UnauthorizedStreamBanner } from './stream-banner/unauthorized-stream-banner.tsx';
import { UnauthenticatedStreamQuery } from './unauthenticated-stream-query.ts';

const StreamPageComponent = () => {
  const streamId = useParams().id as string;
  const { streamQueryRef, authenticated } = useStreamLoaderData();
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;
  const isTablet = useIsTablet();

  const { isEnterprise, isAdmin, id: meId } = useAuthInfo();

  const [interactionModalOpen, setInteractionModalOpen] = useState(false);

  const isAuthor = meId === stream?.author.id;
  const chartsEnabled = useFeatureFlags(flags.streamTimeSeries);
  const { data: timeSeriesData } = useQuery(StreamHasTimeSeriesQuery, {
    variables: { id: streamId },
    fetchPolicy: 'cache-and-network',
    skip: !authenticated || !chartsEnabled || isAuthor,
  });
  const showCharts =
    chartsEnabled && (isAuthor || Boolean(timeSeriesData?.streamHasTimeSeries));

  useEffect(() => {
    if (
      authenticated &&
      stream &&
      getStreamNotifSoundStatus(stream.id) === SoundStatus.on
    )
      checkUserInteractionWithAudio(() => setInteractionModalOpen(true));
  }, [authenticated, setInteractionModalOpen, stream]);

  if (!stream) {
    return null;
  }

  return (
    <>
      <StickyTabNav
        className={tournamentNav}
        size={isTablet ? 'large' : 'small'}
      >
        <TabLink end to={paths.makeStream(streamId)} preventScrollReset>
          Data stream
        </TabLink>
        <TabLink to={paths.makeStreamDiscussions(streamId)} preventScrollReset>
          Discussion
        </TabLink>
        <TabLink
          to={
            meId === stream?.author.id
              ? paths.makeStreamApi(streamId)
              : paths.makeStreamReaderApi(streamId)
          }
          preventScrollReset
        >
          API
        </TabLink>

        {showCharts ? (
          <TabLink to={paths.makeStreamTimeSeries(streamId)} preventScrollReset>
            Charts
          </TabLink>
        ) : null}

        {isEnterprise ? (
          <TabLink
            to={paths.makeStreamRegionStats(streamId)}
            preventScrollReset
          >
            Region Stats
          </TabLink>
        ) : null}

        {isAdmin ? (
          <TabLink
            to={paths.makeStreamStats(streamId)}
            preventScrollReset
            className={adminTab}
          >
            Stats
          </TabLink>
        ) : null}

        {isAdmin ? (
          <TabLink
            to={paths.makeStreamRoutes(streamId)}
            preventScrollReset
            className={adminTab}
          >
            Routes
          </TabLink>
        ) : null}

        {isAdmin ? (
          <TabLink
            to={paths.makeStreamExport(streamId)}
            preventScrollReset
            className={adminTab}
          >
            Enterprise Export
          </TabLink>
        ) : null}
      </StickyTabNav>
      <Outlet />

      {authenticated && (
        <InteractionRequiredModal
          open={interactionModalOpen}
          onOpenChange={setInteractionModalOpen}
        />
      )}
    </>
  );
};

export const clientLoader = streamLoader;
(clientLoader as unknown as ClientLoaderFunction).hydrate = true;

const hasChangedSearchParam = (
  nextUrl: URL,
  currentUrl: URL,
  param: string,
) => {
  return nextUrl.searchParams.get(param) !== currentUrl.searchParams.get(param);
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}) => {
  if (
    currentParams.id === nextParams.id &&
    ['q', 'idempotencyKey', 'region', 'series'].some((param) =>
      hasChangedSearchParam(nextUrl, currentUrl, param),
    )
  ) {
    // skip revalidation when searching for posts, filtering region stats or switching charts
    return false;
  }

  return defaultShouldRevalidate;
};

export const loader = async ({ params, context }: Route.LoaderArgs) => {
  const tokens = await context.get(tokenProviderContext).getTokens();

  if (tokens) {
    return null;
  }

  const id = params.id as string;
  const client = makeSsrApolloClient();
  const { data, errors } = await client.query({
    query: UnauthenticatedStreamQuery,
    variables: { id },
    errorPolicy: 'all',
  });

  const unexpectedError = errors?.find(shouldReportGraphQLError);

  if (unexpectedError) {
    throw unexpectedError;
  }

  return { unauthenticatedData: data };
};

export const meta = ({ loaderData }: Route.MetaArgs) => {
  const stream =
    loaderData &&
    'unauthenticatedData' in loaderData &&
    loaderData.unauthenticatedData?.stream;
  return stream ? getPageMeta(getStreamPageMeta(stream)) : getPageMeta();
};
export const ErrorBoundary = ErrorPage;

export default function Stream() {
  const { authenticated } = useStreamLoaderData();

  const isMobile = !useIsTablet();

  return (
    <Page>
      {authenticated && <PageHeader>Stream</PageHeader>}
      <PageContent>
        {authenticated && !isMobile && (
          <Suspense fallback={<NudgeFallback />}>
            <NudgeCard />
          </Suspense>
        )}
        <Suspense fallback={<StreamBannerFallback />}>
          {authenticated ? <StreamBanner /> : <UnauthorizedStreamBanner />}
        </Suspense>
        <Suspense fallback={null}>
          <StreamPageComponent />
        </Suspense>
      </PageContent>
    </Page>
  );
}

const NudgeCard = () => {
  const { streamQueryRef } =
    useStreamLoaderData() as AuthenticatedStreamLoaderData;
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;

  if (!stream) {
    return null;
  }

  return <StreamDeckNudge />;
};
